import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { OTPInput } from '../../components/ui/OTPInput';
import { AuthService, getAuthErrorMessage } from '../../services/authService';
import { INDIAN_PHONE_ERROR_MESSAGE } from '../../utils/phone';

type Step = 'identify' | 'otp' | 'reset';
type IdentifierType = 'email' | 'phone' | 'unknown';

function normalizeIdentifier(raw: string): { to: string; type: IdentifierType; error?: string } {
  const value = raw.trim();
  if (!value) return { to: '', type: 'unknown', error: 'Email or phone is required' };

  const first = value[0] ?? '';
  const digitsOnly = value.replace(/\D/g, '');
  const looksPhone = /^\d/.test(first) || digitsOnly.length >= 7;

  if (looksPhone) {
    if (!/^[6-9]\d{9}$/.test(digitsOnly)) {
      return { to: digitsOnly, type: 'phone', error: INDIAN_PHONE_ERROR_MESSAGE };
    }
    return { to: digitsOnly, type: 'phone' };
  }

  const looksEmail = /^[A-Za-z]/.test(first) || value.includes('@');
  if (looksEmail) {
    if (!/\S+@\S+\.\S+/.test(value)) {
      return { to: value, type: 'email', error: 'Email is invalid' };
    }
    return { to: value, type: 'email' };
  }

  return { to: value, type: 'unknown', error: 'Please enter a valid email or Indian mobile number' };
}

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('identify');
  const [identifier, setIdentifier] = useState('');
  const [to, setTo] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const normalized = useMemo(() => normalizeIdentifier(identifier), [identifier]);

  const validateIdentify = () => {
    const nextErrors: Record<string, string> = {};
    if (normalized.error) nextErrors.identifier = normalized.error;
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateOtp = (otpValue: string) => {
    const nextErrors: Record<string, string> = {};
    if (!otpValue) nextErrors.otp = 'Please enter the 4-digit OTP';
    else if (!/^\d{4}$/.test(otpValue)) nextErrors.otp = 'Please enter a valid 4-digit OTP';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateReset = () => {
    const nextErrors: Record<string, string> = {};
    if (!password) nextErrors.password = 'Password is required';
    if (!confirmPassword) nextErrors.confirmPassword = 'Confirm password is required';
    if (password && confirmPassword && password !== confirmPassword) nextErrors.confirmPassword = 'Passwords do not match';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validateIdentify()) {
      if (normalized.error) toast.error(normalized.error);
      return;
    }
    setIsLoading(true);
    setErrors({});
    try {
      await AuthService.requestPasswordResetOtp(normalized.to);
      setTo(normalized.to);
      setStep('otp');
      toast.success('OTP sent successfully');
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'Failed to send OTP. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otpValue?: string) => {
    const value = (otpValue ?? otp).trim();
    if (!validateOtp(value)) return;
    if (!to) {
      toast.error('Missing email/phone. Please start again.');
      setStep('identify');
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      await AuthService.verifyPasswordResetOtp(to, value);
      setOtp(value);
      setStep('reset');
      toast.success('OTP verified');
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'Invalid or expired OTP. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validateReset()) {
      if (password && confirmPassword && password !== confirmPassword) {
        toast.error('Passwords do not match. Please try again.');
      }
      return;
    }
    if (!to) {
      toast.error('Missing email/phone. Please start again.');
      setStep('identify');
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      await AuthService.resetPasswordPut(to, password);
      toast.success('Password changed successfully. Please sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'Failed to change password. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout showImage={false} backgroundVariant="spotlight" transitionKey={step}>
      <div className="space-y-4 px-4 pt-8 pb-2 lg:px-0 lg:py-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">
            Forgot Password
          </h1>
          <Link to="/login" className="text-sm font-medium text-[#8E8E93] hover:text-white transition-colors">
            Back to login
          </Link>
        </div>
        <p className="text-sm text-[#8E8E93]">
          {step === 'identify' && 'Enter your email or phone number to receive an OTP.'}
          {step === 'otp' && 'Enter the 4-digit OTP sent to you.'}
          {step === 'reset' && 'Set a new password for your account.'}
        </p>

        {step === 'identify' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendOtp();
            }}
          >
            <Input
              label="Email or phone"
              placeholder="Enter email or 10-digit phone"
              value={identifier}
              onChange={(e) => {
                const raw = e.target.value;
                const trimmed = raw.trimStart();
                const first = trimmed[0] ?? '';
                const isLikelyPhone = /^\d/.test(first) || raw.replace(/\D/g, '').length >= 7;
                if (isLikelyPhone) {
                  const digits = raw.replace(/\D/g, '').slice(0, 10);
                  setIdentifier(digits);
                } else {
                  setIdentifier(raw);
                }
                if (errors.identifier) setErrors((prev) => ({ ...prev, identifier: '' }));
              }}
              error={errors.identifier}
            />

            <Button type="submit" variant="active" className="w-full" isLoading={isLoading}>
              Send OTP
            </Button>
          </form>
        )}

        {step === 'otp' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleVerifyOtp();
            }}
          >
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">Enter OTP</label>
              <OTPInput
                length={4}
                value={otp}
                onChange={(value) => {
                  const trimmed = value.trim();
                  setOtp(trimmed);
                  if (errors.otp) setErrors((prev) => ({ ...prev, otp: '' }));
                }}
                onComplete={(value) => {
                  const trimmed = value.trim();
                  setOtp(trimmed);
                  if (trimmed.length === 4 && /^\d{4}$/.test(trimmed)) {
                    handleVerifyOtp(trimmed);
                  }
                }}
                autoFocus
              />
              {errors.otp && <p className="text-xs text-destructive">{errors.otp}</p>}
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-sm font-medium text-[#F33363] hover:text-[#D92D58] transition-colors"
                onClick={() => {
                  setStep('identify');
                  setOtp('');
                  setErrors({});
                }}
                disabled={isLoading}
              >
                Change email/phone
              </button>
              <button
                type="button"
                className="text-sm font-medium text-[#F33363] hover:text-[#D92D58] transition-colors"
                onClick={handleSendOtp}
                disabled={isLoading}
              >
                Resend OTP
              </button>
            </div>

            <Button type="submit" variant="active" className="w-full" isLoading={isLoading}>
              Verify OTP
            </Button>
          </form>
        )}

        {step === 'reset' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleResetPassword();
            }}
          >
            <Input
              label="Password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
              }}
              error={errors.password}
              showPasswordToggle
            />
            <Input
              label="Confirm password"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
              }}
              error={errors.confirmPassword}
              showPasswordToggle
            />

            <Button type="submit" variant="active" className="w-full" isLoading={isLoading}>
              Change password
            </Button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

